import { Box, Card, Typography, Link as LinkButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { useEffect } from 'react';
import { BackorderedKit, BackorderedKitItem } from './types';
import styles from './Shipments.module.css';
import { itemImages, kitImages } from '../../../helpers/image';
import { getBackorderedKits, selectBackorderedKits } from './slice';
import useCustomerNumber from '../../../hooks/useCustomerNumber';

const BackorderedItems = () => {
  const customerNumber = useCustomerNumber()
  const backorderedKits = useAppSelector(selectBackorderedKits);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (customerNumber) {
      dispatch(getBackorderedKits(customerNumber));
    }
  }, [customerNumber]);
  
  const getKitColor = (kit: BackorderedKit) => {
    const status = kit.refillStatus

    const colorByStatus = new Map([
      [1, '#27c24c'],
      [2, '#F4B122'],
      [4, '#27c24c'],
    ])

    const color = colorByStatus.get(status)

    return color
  }

  const getKitMessage = (kit: BackorderedKit) => {
    const status = kit.refillStatus

    const messageByStatus = new Map([
      [1, 'Automatic Refill On'],
      [2, 'Refill on Approval'],
      [4, 'On Membership'],
    ])

    const message = messageByStatus.get(status) ?? 'Automatic Refill Off'

    return message
  }

  const getItemColor = (item: BackorderedKitItem) => {
    const code = item.complianceCode

    const colorByCode = new Map([
      ['EPREP_MED_EXPIRED_BACKORDERED', '#f05050'],
      ['EPREP_MED_EXPIRED_REFILL_SOON', '#f05050'],
      ['EPREP_MED_EXPIRING_BACKORDERED', '#F4B122'],
      ['EPREP_MED_UNSHIPPED', '#F4B122'],
    ])

    const color = colorByCode.get(code) ?? null

    return color
  }

  const getItemMessage = (item: BackorderedKitItem) => {
    const code = item.complianceCode

    const date = item.expirationDate
      ? new Date(item.expirationDate).toDateString()
      : ''

    const messageByCode = new Map([
      ['EPREP_MED_EXPIRED_BACKORDERED', `Expired ${date}`],
      ['EPREP_MED_EXPIRED_REFILL_SOON',  `Expired ${date}`],
      ['EPREP_MED_EXPIRING_BACKORDERED', `Expiring ${date}`],
      ['EPREP_MED_UNSHIPPED', 'Not yet Shipped'],
    ])

    const message = messageByCode.get(code) ?? null

    return message
  }  

  const getRefillMessage = (refillStatus: number, complianceCode: string) => {
    if (refillStatus === 0) {
      return 'To order item please contact Customer Service.';
    } else if (refillStatus === 1 && complianceCode !== 'EPREP_MED_EXPIRED_REFILL_SOON') {
      return 'Item will be shipped when available.';
    } else if (refillStatus === 1 && complianceCode === 'EPREP_MED_EXPIRED_REFILL_SOON') {
      return 'Item is scheduled to be refilled soon.';
    } else if (refillStatus === 2) {
      return 'Purchase order will be sent when items are available.';
    } else if (refillStatus === 3) {
      return 'Item will be shipped when available.';
    }
  };

  return (
    <>
      {backorderedKits && (
        <Box sx={{ p: 2 }}>
          <Box className={styles.alert} sx={{ mb: 2, p: 2 }}>
            <Typography style={{ fontSize: '16px' }}>
              Backordered items are unavailable because of a national shortage from the manufacturer(s). For more information on national drug
              shortages, visit the{' '}
              <LinkButton
                component="button"
                onClick={() => {
                  window.open('https://dps.fda.gov/drugshortages', '_blank');
                }}
              >
                FDA's website
              </LinkButton>
              .
            </Typography>
          </Box>

          <Box style={{ marginBottom: '20px' }}>
            {Object.keys(backorderedKits).map((kitId) => {
              const kit: BackorderedKit = backorderedKits[kitId];

              const kitImage = kit.kitImage ? `${kit.kitImage}.png` : 'default-kit-new.png'
              const color = getKitColor(kit)
              const message = getKitMessage(kit)

              return (
                <Box key={kitId}>
                  <Card variant="outlined">
                    <Box className={styles.boxHeader}>
                      <Box style={{ padding: '30px' }}>
                        <Box style={{ display: 'flex', flexDirection: 'row' }}>
                          <Box style={{ marginRight: '15px' }}>
                            <img src={kitImages[kitImage]} alt="kit" style={{ width: '65px' }} />
                          </Box>
                          <Box>
                            <Typography variant="h3" style={{ fontSize: '23px' }}>
                              {kit.model ?? ''}
                            </Typography>
                            <Typography style={{ fontSize: '13px' }}>
                              <span style={{ color }}>
                                {message}
                              </span>
                            </Typography>
                            <Typography>Kit # {kit.serialNumber}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box style={{ padding: '20px' }}>
                      {kit.items.map((item, i) => {
                        const itemImage = item.itemImage ? `${item.itemImage}.jpg` : 'no_image.png'
                        const color = getItemColor(item)
                        const message = getItemMessage(item)

                        return (
                          <Box key={i} className="kit-item" style={{ display: 'flex' }}>
                            <Box style={{ marginRight: '15px' }}>
                              <img style={{ maxHeight: '50px' }} src={itemImages[itemImage]} alt="item" />
                            </Box>
                            <Box style={{ alignContent: 'center' }}>
                              <Typography style={{ fontSize: '13px' }}>
                                {item.quantity} {item.itemName}
                                {item.lotNumber && <span style={{ color: '#BFC6C8', marginLeft: '10px' }}>#{item.lotNumber}</span>}
                              </Typography>
                              <Box>
                                {
                                  color && message
                                    ? (
                                      <Typography style={{ color, fontSize: '13px' }}>
                                        { message }
                                      </Typography>
                                    )
                                    : null
                                }
                              </Box>
                              <Typography style={{ fontSize: '13px' }}>
                                {getRefillMessage(item.refillStatus, item.complianceCode)}
                              </Typography>
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default BackorderedItems;
